import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Paper,
  Grid,
  Button,
  Divider,
  Avatar,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  TextField,
  InputAdornment,
  CircularProgress,
} from "@mui/material";
import {
  PlayArrow as PlayArrowIcon,
  Pause as PauseIcon,
  PersonOutline as PersonOutlineIcon,
  AccessTime as AccessTimeIcon,
  Search as SearchIcon,
  Close as CloseIcon,
  AudioFile as AudioFileIcon,
} from "@mui/icons-material";
import api from "../../../conf/api";

const AudioCard = ({ audio, isSelected, onSelect }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [audioElement] = useState(audio.file ? new Audio(audio.file) : null);

  const togglePlayPause = (e) => {
    e.stopPropagation();
    if (!audioElement) return;
    
    if (isPlaying) {
      audioElement.pause();
    } else {
      audioElement.play();
    }
    setIsPlaying(!isPlaying);
  };

  const formatDuration = (duration) => {
    const minutes = Math.floor(duration / 60);
    const seconds = Math.floor(duration % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  const getStatusColor = (approved, rejected) => {
    if (approved) return "success";
    if (rejected) return "error";
    return "warning";
  };

  const getStatusLabel = (approved, rejected) => {
    if (approved) return "Approved";
    if (rejected) return "Rejected";
    return "Pending";
  };

  return (
    <Paper
      sx={{
        p: 2,
        cursor: "pointer",
        border: isSelected ? 2 : 1,
        borderColor: isSelected ? "primary.main" : "divider",
        backgroundColor: isSelected ? "primary.50" : "background.paper",
        "&:hover": {
          backgroundColor: isSelected ? "primary.100" : "grey.50",
        },
      }}
      onClick={() => onSelect(audio)}
    >
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 1 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, flex: 1 }} noWrap title={audio.title}>
          {audio.title}
        </Typography>
        {audioElement && (
          <IconButton size="small" onClick={togglePlayPause}>
            {isPlaying ? <PauseIcon /> : <PlayArrowIcon />}
          </IconButton>
        )}
      </Box>

      <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
        <Avatar sx={{ width: 20, height: 20, mr: 1, bgcolor: "primary.light" }}>
          <PersonOutlineIcon sx={{ fontSize: 12 }} />
        </Avatar>
        <Typography variant="body2" color="textSecondary" noWrap>
          {audio.username}
        </Typography>
      </Box>

      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        {audio.duration > 0 && (
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <AccessTimeIcon sx={{ fontSize: 16, mr: 0.5, color: "text.secondary" }} />
            <Typography variant="body2" color="textSecondary">
              {formatDuration(audio.duration)}
            </Typography>
          </Box>
        )}
        
        <Chip
          label={getStatusLabel(audio.approved, audio.rejected)}
          color={getStatusColor(audio.approved, audio.rejected)}
          size="small"
        />
      </Box>
    </Paper>
  );
};

const AudioSelector = ({ open, onClose, onSelect, selectedAudio }) => {
  const [audioList, setAudioList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);

  const fetchAudioList = async (reset = false) => {
    if (loading) return;
    
    setLoading(true);
    try {
      const response = await api.get("/videos/audio/", {
        params: {
          page: reset ? 1 : page,
          search: searchQuery,
          page_size: 12,
        },
      });
      
      if (response.data && response.data.results) {
        if (reset) {
          setAudioList(response.data.results);
          setPage(1);
        } else {
          setAudioList(prev => [...prev, ...response.data.results]);
        }
        
        setHasMore(!!response.data.next);
      } else {
        setAudioList(Array.isArray(response.data) ? response.data : []);
        setHasMore(false);
      }
    } catch (error) {
      console.error("Failed to fetch audio files:", error);
      setAudioList([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      fetchAudioList(true);
    }
  }, [open, searchQuery]);

  const handleLoadMore = () => {
    setPage(prev => prev + 1);
    fetchAudioList(false);
  };

  const handleSelect = (audio) => {
    onSelect(audio);
    onClose();
  };

  const handleClearSelection = () => {
    onSelect(null);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { borderRadius: 2, minHeight: "60vh" } }}
    >
      <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <AudioFileIcon sx={{ mr: 1, color: "primary.main" }} />
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Select Audio Track
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 2 }}>
        <Box sx={{ mb: 3 }}>
          <TextField
            fullWidth
            placeholder="Search audio files..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="primary" />
                </InputAdornment>
              ),
            }}
            sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
          />
        </Box>

        {selectedAudio && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
              Currently Selected:
            </Typography>
            <AudioCard
              audio={selectedAudio}
              isSelected={true}
              onSelect={() => {}}
            />
            <Divider sx={{ my: 2 }} />
          </Box>
        )}

        {loading && audioList.length === 0 && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {!loading && audioList.length === 0 && (
          <Box sx={{ textAlign: "center", py: 4 }}>
            <AudioFileIcon sx={{ fontSize: 64, color: "text.secondary", mb: 2 }} />
            <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
              No Audio Files Found
            </Typography>
            <Typography variant="body1" color="textSecondary">
              {searchQuery 
                ? `No audio files match your search for "${searchQuery}"`
                : "No audio files available"
              }
            </Typography>
          </Box>
        )}

        {audioList.length > 0 && (
          <>
            <Grid container spacing={2}>
              {audioList.map((audio) => (
                <Grid item xs={12} sm={6} key={audio.id}>
                  <AudioCard
                    audio={audio}
                    isSelected={selectedAudio?.id === audio.id}
                    onSelect={handleSelect}
                  />
                </Grid>
              ))}
            </Grid>

            {hasMore && (
              <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
                <Button
                  variant="outlined"
                  onClick={handleLoadMore}
                  disabled={loading}
                  sx={{ borderRadius: 2 }}
                >
                  {loading ? <CircularProgress size={24} /> : "Load More"}
                </Button>
              </Box>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 3 }}>
        {selectedAudio && (
          <Button onClick={handleClearSelection} color="error" sx={{ borderRadius: 2 }}>
            Clear Selection
          </Button>
        )}
        <Button onClick={onClose} sx={{ borderRadius: 2 }}>
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AudioSelector;